import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { useStable } from "../context/StableContext";
import api, { resolveImage, errText } from "../lib/api";
import { processImage } from "../lib/imageResize";
import { Autocomplete } from "../components/Autocomplete";
import { PersonSearchCreate } from "../components/PersonSearchCreate";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateField } from "../components/DateField";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ArrowLeft, Upload, Loader2, Camera, X, Copy } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  photo: "", display_name: "", official_name: "", microchip: "", ueln: "", passport_number: "",
  dob: "", sex: "", breed: "", coat: "", owner_name: "", category: "", presence_status: "presente",
  usable_in_lessons: true, notes: "", destinazione: "non_specificato",
};

export default function HorseForm() {
  const { current, perms } = useStable();
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();
  const camRef = useRef();
  const [form, setForm] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [origDest, setOrigDest] = useState("non_specificato");
  const [ownerSel, setOwnerSel] = useState(null);

  useEffect(() => {
    if (!current) return;
    api.get(`/stables/${current.id}/horse-categories`).then((r) => setCats((r.data || []).filter((c) => !c.archived)));
    if (id) {
      api.get(`/stables/${current.id}/horses/${id}`).then((r) => {
        const d = r.data;
        setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, d[k] ?? ""])),
          usable_in_lessons: d.usable_in_lessons !== false, destinazione: d.destinazione || "non_specificato" });
        setOrigDest(d.destinazione || "non_specificato");
        setOwnerSel(d.owner_id || d.owner_person_id || d.owner_organization_id || d.owner_name
          ? { person_id: d.owner_person_id || null, organization_id: d.owner_organization_id || null, owner_id: d.owner_id || null, label: d.owner_name || "" }
          : null);
        setLoading(false);
      });
    }
  }, [current, id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      let toSend;
      try {
        toSend = await processImage(file);
      } catch (perr) {
        if (perr.message === "HEIC") {
          toast.error("Formato HEIC non supportato dal browser. Converti la foto in JPG e riprova.");
        } else {
          toast.error("Impossibile leggere l'immagine. Usa un file JPG, PNG o WebP.");
        }
        return;
      }
      const fd = new FormData();
      fd.append("file", toSend);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo", data.path);
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = k === "display_name" ? payload[k] : null; });
    payload.display_name = form.display_name;
    payload.presence_status = form.presence_status || "presente";
    payload.usable_in_lessons = !!form.usable_in_lessons;
    const dest = form.destinazione || "non_specificato";
    delete payload.destinazione;  // routed via dedicated endpoint so the change is recorded in history
    // PIM 3C — owner via Search-or-Create: send structured refs (owner_id/owner_person_id) with
    // owner_name as the legacy display fallback. Backend precedence: owner_id > owner_person_id > owner_name.
    payload.owner_name = ownerSel?.label || null;
    payload.owner_id = ownerSel?.owner_id || null;
    payload.owner_person_id = ownerSel?.person_id || null;
    payload.owner_organization_id = ownerSel?.organization_id || null;
    try {
      let horseId = id;
      if (id) await api.put(`/stables/${current.id}/horses/${id}`, payload);
      else { const { data } = await api.post(`/stables/${current.id}/horses`, payload); horseId = data.id; }
      if (id ? dest !== origDest : dest !== "non_specificato") {
        await api.post(`/stables/${current.id}/horses/${horseId}/destinazione`, { destinazione: dest });
      }
      toast.success(id ? "Cavallo aggiornato" : "Cavallo aggiunto");
      navigate(id ? `/cavalli/${id}` : "/cavalli");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1B3B2B]" /></div>;
  if (!perms.canWrite) return <Navigate to="/cavalli" replace />;

  const preview = resolveImage(form.photo);

  return (
    <div className="max-w-3xl mx-auto eq-fade-up">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#5F6B64] mb-4" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Indietro
      </button>
      <h1 className="font-display text-3xl font-bold text-[#1B3B2B] mb-6">
        {id ? "Modifica cavallo" : "Nuovo cavallo"}
      </h1>

      <form onSubmit={submit} className="bg-white rounded-xl border border-[#E5DEC9] p-6 space-y-6">
        {/* Photo */}
        <div className="flex items-center gap-4">
          <div className="h-24 w-24 rounded-xl bg-[#F0EAE1] overflow-hidden flex items-center justify-center shrink-0">
            {preview ? <img src={preview} alt="anteprima" className="h-full w-full object-cover" data-testid="horse-photo-preview" />
              : <span className="text-[#8C532B] font-display font-bold">Foto</span>}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" hidden onChange={upload} data-testid="horse-photo-input" />
            <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={upload} data-testid="horse-photo-camera" />
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current.click()} disabled={uploading} data-testid="upload-photo-btn">
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                {preview ? "Sostituisci" : "Carica foto"}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => camRef.current.click()} disabled={uploading} data-testid="camera-photo-btn" className="sm:hidden">
                <Camera className="h-4 w-4 mr-2" /> Fotocamera
              </Button>
              {preview && (
                <Button type="button" variant="ghost" size="sm" onClick={() => set("photo", "")} data-testid="remove-photo-btn" className="text-red-600">
                  <X className="h-4 w-4 mr-1" /> Rimuovi
                </Button>
              )}
            </div>
            <p className="text-xs text-[#8FA396]">JPG, PNG o WebP. L'immagine viene ridimensionata automaticamente.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nome (scuderia) *">
            <Input required data-testid="f-display_name" value={form.display_name} onChange={(e) => set("display_name", e.target.value)} />
            {form.official_name && form.official_name !== form.display_name && (
              <button type="button" data-testid="use-official-name" onClick={() => set("display_name", form.official_name)}
                className="mt-1 inline-flex items-center gap-1 text-xs text-[#8C532B] hover:underline">
                <Copy className="h-3 w-3" /> Usa nome ufficiale
              </button>
            )}
          </Field>
          <Field label="Nome ufficiale registrato">
            <Input data-testid="f-official_name" value={form.official_name} onChange={(e) => set("official_name", e.target.value)} />
            {form.display_name && form.display_name !== form.official_name && (
              <button type="button" data-testid="copy-from-stable-name" onClick={() => set("official_name", form.display_name)}
                className="mt-1 inline-flex items-center gap-1 text-xs text-[#8C532B] hover:underline">
                <Copy className="h-3 w-3" /> Copia dal nome in scuderia
              </button>
            )}
          </Field>
          <Field label="Microchip"><Input data-testid="f-microchip" value={form.microchip} onChange={(e) => set("microchip", e.target.value)} /></Field>
          <Field label="UELN"><Input data-testid="f-ueln" value={form.ueln} onChange={(e) => set("ueln", e.target.value)} /></Field>
          <Field label="N° passaporto / documento"><Input data-testid="f-passport" value={form.passport_number} onChange={(e) => set("passport_number", e.target.value)} /></Field>
          <Field label="Data di nascita"><DateField testId="f-dob" value={form.dob || ""} onChange={(v) => set("dob", v)} /></Field>
          <Field label="Sesso">
            <Select value={form.sex || ""} onValueChange={(v) => set("sex", v)}>
              <SelectTrigger data-testid="f-sex"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>
                {["Maschio", "Femmina", "Castrone", "Stallone"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Razza">
            <Autocomplete testId="f-breed" value={form.breed} onChange={(v) => set("breed", v)} placeholder="Scrivi o scegli…"
              fetcher={(q) => api.get("/dictionaries/breeds", { params: { q } }).then((r) => r.data)} />
          </Field>
          <Field label="Mantello / colore">
            <Autocomplete testId="f-coat" value={form.coat} onChange={(v) => set("coat", v)} placeholder="Scrivi o scegli…"
              fetcher={(q) => api.get("/dictionaries/coats", { params: { q } }).then((r) => r.data)} />
          </Field>
          <Field label="Proprietario">
            <PersonSearchCreate stableId={current.id} value={ownerSel} onChange={setOwnerSel}
              entities={["person", "organization"]} placeholder="Cerca persona o organizzazione…" />
          </Field>
          <Field label="Categoria">
            <Select value={form.category || ""} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="f-category"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>
                {cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Destinazione (DPA/NDPA)">
            <Select value={form.destinazione || "non_specificato"} onValueChange={(v) => set("destinazione", v)}>
              <SelectTrigger data-testid="f-destinazione"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="non_specificato">Non specificato</SelectItem>
                <SelectItem value="dpa">DPA (destinato alla produzione alimentare)</SelectItem>
                <SelectItem value="ndpa">NDPA (non destinato)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Presenza">
            <Select value={form.presence_status} onValueChange={(v) => set("presence_status", v)}>
              <SelectTrigger data-testid="f-presence"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="presente">Presente</SelectItem>
                <SelectItem value="assente_temporaneo">Temporaneamente assente</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Disponibile per la scuola">
            <label className="flex items-center gap-3 h-10 cursor-pointer">
              <Switch data-testid="f-usable-in-lessons" checked={form.usable_in_lessons}
                onCheckedChange={(v) => set("usable_in_lessons", v)} />
              <span className="text-sm text-[#5F6B64]">{form.usable_in_lessons ? "Proposto per la scuola" : "Solo col proprietario/allievo"}</span>
            </label>
          </Field>
        </div>

        <Field label="Note generali"><Textarea data-testid="f-notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Annulla</Button>
          <Button type="submit" disabled={saving} data-testid="save-horse-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[#2C4A38]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
